import type { ReactNode } from 'react'
import { Menu, toastManager } from '@systemeio/components'
import { Pin02Icon } from '@systemeio/icons'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { twJoin } from 'tailwind-merge'
import useUser from 'shared/hooks/use-user'
import { communityPostKeys } from 'modules/community/api/key-factory'
import { usePinPost } from 'modules/community/api/use-pin-post'
import { mutatePostsByPin } from 'modules/community/components/pinned-post/utils'
import useIsUserMember from 'modules/community/hooks/use-is-user-member'
import type { PostInterface } from 'modules/community/types/post-interface'
import type { PostPinnedToType } from 'modules/community/types/post-pinned-to-type'
import { hasPermissions } from 'modules/community/utils/abac-roles-permissions'
import { isPinnedIn } from 'modules/community/utils/pinned-post'

interface PostPinnedActionsProps {
  post: PostInterface
}

export const PostPinnedActions = ({ post }: PostPinnedActionsProps): ReactNode => {
  const { t } = useTranslation()
  const { query } = useRouter()
  const { communityPath, topicPath } = query
  const { user } = useUser()
  const { member } = useIsUserMember()
  const queryClient = useQueryClient()
  const pinPostMutation = usePinPost({ postId: post.id })

  const handlePinPost = async (post: PostInterface, pinTo: PostPinnedToType): Promise<void> => {
    if (communityPath == null || Array.isArray(communityPath)) return

    try {
      await pinPostMutation.trigger({ source: pinTo })

      const shouldRemoveFromGlobal =
        (pinTo === 'community' && !topicPath) || (pinTo === 'topic' && !!topicPath)

      queryClient.setQueryData(
        communityPostKeys.pinnedList(communityPath, topicPath),
        mutatePostsByPin({
          post,
          shouldAppend: shouldRemoveFromGlobal,
          modifier: pinnedTo => [...pinnedTo, pinTo],
        }),
      )

      if (shouldRemoveFromGlobal) {
        const listKey = topicPath
          ? communityPostKeys.topicList(communityPath, topicPath)
          : communityPostKeys.list(communityPath)

        queryClient.setQueriesData<{ pages: { items: PostInterface[] }[] }>(
          { queryKey: listKey },
          old => {
            if (!old || !('pages' in old)) return old
            return {
              ...old,
              pages: old.pages.map(page => ({
                ...page,
                items: page.items.filter(p => p.id !== post.id),
              })),
            }
          },
        )
      }

      toastManager.add({ type: 'success', title: t('community.actions.pin_post.success') })
    } catch {
      toastManager.add({ type: 'error', title: t('community.actions.pin_post.error') })
    }
  }

  const [isPostPinnable] = hasPermissions(user, 'posts', ['pin'], { post, member })

  if (!isPostPinnable) return null

  return (
    <div className="flex items-center">
      <div className="relative flex size-7.5" onClick={e => e.stopPropagation()}>
        <Menu
          trigger={
            <button type="button">
              <Pin02Icon className="size-4" />
            </button>
          }
          triggerProps={{ className: 'size-7' }}
          items={(['community', 'topic'] as PostPinnedToType[]).map(source => {
            const isPinned = isPinnedIn(post, source)
            return {
              id: `pin-target-${source}`,
              className: 'hover:text-text-primary/70',
              label: (
                <span className="flex w-full items-center gap-1.5">
                  <Pin02Icon
                    aria-hidden
                    className={twJoin(
                      'size-4 text-text-secondary opacity-0',
                      isPinned && 'opacity-100',
                    )}
                  />
                  <span className="mr-auto">{t(`community.actions.pin_post.${source}`)}</span>
                </span>
              ),
              disabled: isPinned,
              onSelect: () => {
                void handlePinPost(post, source)
              },
            }
          })}
        />
      </div>
    </div>
  )
}
